import {IconButton} from '@dropbox/dig-components/buttons';
import {atoms, Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AiSearchLine, CloseLine} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {KeyboardEvent, MouseEvent, ReactNode, RefObject, useEffect, useRef} from 'react';

import styles from './SearchBox.module.css';

// One line that says both jobs: people search still works (almost every pick
// is a person), and you can now ask about company knowledge.
const placeholder = () => t('search_box_placeholder');

/**
 * The search glyph, the field and anything trailing it, as one row: the box's
 * header, and the home field that opens it, so the two line up exactly.
 */
const FieldRow = ({field, trailing}: {field: ReactNode; trailing?: ReactNode}) => (
  <Split gap="12" alignY="center">
    <Split.Item>
      <UIIcon src={AiSearchLine} size="large" className={atoms({color: 'Text Subtle'})} />
    </Split.Item>
    <Split.Item width="fill" style={{minWidth: 0}}>
      {field}
    </Split.Item>
    {trailing && <Split.Item>{trailing}</Split.Item>}
  </Split>
);

/** The box's header: the field itself, no box inside the box, like Raycast. */
export const SearchInput = ({
  query,
  setQuery,
  inputRef,
}: {
  query: string;
  setQuery: (query: string) => void;
  inputRef: RefObject<HTMLInputElement>;
}) => (
  <FieldRow
    field={
      <input
        ref={inputRef}
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        placeholder={placeholder()}
        aria-label={placeholder()}
        autoFocus
        autoComplete="off"
        className={`${styles.input} ${atoms({color: 'Text Base'})}`}
      />
    }
    // Once there's text, an X clears it and keeps you typing.
    trailing={
      query && (
        <IconButton
          variant="borderless"
          size="small"
          shape="circular"
          aria-label={t('clear')}
          onClick={() => {
            setQuery('');
            inputRef.current?.focus();
          }}
        >
          <UIIcon src={CloseLine} />
        </IconButton>
      )
    }
  />
);

/**
 * The home page's search field, with the relaunch: drawn like the box's
 * header (same height, glyph, text position and size), so when the box opens
 * on top of it nothing moves. It only opens the box, so it's a button, not an
 * input: clicking anywhere on it, Enter or Space, or starting to type opens
 * search, and there's no caret or text to select.
 */
export const HomeSearchField = ({onOpen, autoFocus}: {onOpen: () => void; autoFocus: boolean}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label={placeholder()}
      className={`${styles.homeField} ${atoms({borderRadius: 'Large'})}`}
      onClick={onOpen}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.altKey ||
          event.key.startsWith('Arrow') ||
          ['Tab', 'Escape', 'Shift'].includes(event.key)
        ) {
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') event.preventDefault();
        onOpen();
      }}
    >
      <FieldRow
        field={<div className={`${styles.input} ${styles.placeholder}`}>{placeholder()}</div>}
      />
    </div>
  );
};

/**
 * The box itself, floating (or `flat`, filling a window that's already its
 * own, like the installed search app: no corners, shadow or max width): the input as its header (inset so the search
 * glyph, which sits ~6px inside its box, lines up with the body's 24px
 * edge), then the body. Centered in whatever holds it; `width` can change and
 * animates. Keys pressed anywhere inside reach `onKeyDown`, and the box can
 * hold focus itself (clicking a blank part of it), so the arrow keys never
 * fall through to the page behind.
 */
export const ModalShell = ({
  width,
  flat,
  input,
  onKeyDown,
  onMouseUp,
  children,
}: {
  width: number;
  flat: boolean;
  input: ReactNode;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onMouseUp: (event: MouseEvent<HTMLDivElement>) => void;
  children: ReactNode;
}) => (
  <Box
    backgroundColor="Background Base"
    borderRadius={flat ? undefined : 'Large'}
    boxShadow={flat ? undefined : 'Floating'}
    width="100%"
    tabIndex={-1}
    onKeyDown={onKeyDown}
    onMouseUp={onMouseUp}
    className={styles.shell}
    style={flat ? undefined : {maxWidth: width}}
  >
    <Box paddingY="16" className={`${styles.header} ${styles.headerInset}`}>
      {input}
    </Box>
    {children}
  </Box>
);
